import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["lesson"];
/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import * as KeyCodes from 'UIComponents/constants/KeyCodes';
import { Flex } from 'foundations-components/components/Flex';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { reportErrorToLogFetch } from 'academy-common-lib/utils/errorUtils';
import QuizQuestionContainer from './QuizQuestionContainer';
import QuizEndScreen from './QuizEndScreen';
import QuizTopbar from '../../../components/QuizTopbar';
import QuizError from './QuizError';
import QuizControlBar from './QuizControlBar';
import { DEFAULT_LANGUAGE } from '../../../constants/ApiConstants';
import { useUpdateContentProgress } from '../../userProgress/';
import { useClearQuizAttempt, useFinishQuiz, useStartQuiz, useSubmitAnswer } from '../quizQueries';
import sortBy from 'hs-lodash/sortBy';
import { useUnmountLifecycle } from '../../../hooks/useUnmountLifecycle';
import UsageTracker from '../../../utils/UsageTracker';
import { Metrics } from '../../../Metrics';
import { ContactUsLink } from '../../../components/ContactUsLink';
import { QuizWrapper } from '../../exams/components/QuizWrapper';
import { Link } from '../../../components/AcademyLinks';
import { isQuizAsset } from '../../tracks/tracksUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useQuizContainer = ({
  quiz,
  contentProgress,
  lessonProgress
}) => {
  const contentStatus = contentProgress === null || contentProgress === void 0 ? void 0 : contentProgress.progressType;
  const recentQuizAttemptId = lessonProgress === null || lessonProgress === void 0 ? void 0 : lessonProgress.quizIdToRecentAttemptId[quiz.id];
  const recentQuizAttempt = recentQuizAttemptId ? lessonProgress === null || lessonProgress === void 0 ? void 0 : lessonProgress.quizAttempts[recentQuizAttemptId] : null;
  const questions = useMemo(() => {
    if (isQuizAsset(quiz)) {
      return sortBy([...quiz.questions], q => q.displayOrder).map(q => Object.assign({}, q, {
        answers: sortBy([...q.answers], a => a.displayOrder)
      }));
    }
    return [];
  }, [quiz]);
  return {
    contentStatus,
    recentQuizAttempt,
    questions
  };
};
const QuizContainer = ({
  track,
  lesson,
  content,
  contentProgress,
  lessonProgress,
  onFinish
}) => {
  const quiz = content;
  const quizId = quiz.id;
  const lessonId = lesson.id;
  const trackId = track === null || track === void 0 ? void 0 : track.id;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState([]);
  const {
    contentStatus,
    recentQuizAttempt,
    questions
  } = useQuizContainer({
    quiz: isQuizAsset(content) ? content : {},
    contentProgress,
    lessonProgress
  });
  const [startQuiz, {
    startQuizLoading,
    startQuizError
  }] = useStartQuiz();
  const [submitQuizAnswer, {
    submitQuizAnswerLoading,
    submitQuizAnswerError
  }] = useSubmitAnswer();
  const [finishQuiz, {
    finishQuizLoading,
    finishQuizError
  }] = useFinishQuiz();
  const [updateContentProgress, {
    updateContentProgressLoading,
    updateContentProgressError
  }] = useUpdateContentProgress();
  const clearQuizAttempt = useClearQuizAttempt();
  const contentLanguage = (content === null || content === void 0 ? void 0 : content.language) || DEFAULT_LANGUAGE;
  const hasCompleted = contentStatus && contentStatus === 'COMPLETED';
  const maybeAnswerSubmissions = recentQuizAttempt === null || recentQuizAttempt === void 0 ? void 0 : recentQuizAttempt.answerSubmissions;
  const currentAttemptId = recentQuizAttempt === null || recentQuizAttempt === void 0 ? void 0 : recentQuizAttempt.id;
  const currentQuestion = questions && questions[currentQuestionIndex];
  const error = startQuizError || submitQuizAnswerError || finishQuizError || updateContentProgressError;
  const loading = startQuizLoading || submitQuizAnswerLoading || finishQuizLoading || updateContentProgressLoading;
  const getHasAnsweredCurrentQuestion = useCallback(questionId => {
    return !!(maybeAnswerSubmissions && maybeAnswerSubmissions.find(submission => submission.questionId === questionId));
  }, [maybeAnswerSubmissions]);
  const isProgressButtonDisabled = !!error || !hasCompleted && (loading || selectedAnswerIds.length < 1);
  const finaliseAttempt = useCallback(() => {
    if (!currentAttemptId) {
      return null;
    }
    return finishQuiz(lessonId, quizId, currentAttemptId, track === null || track === void 0 ? void 0 : track.id).then(() => {
      updateContentProgress({
        trackId,
        lessonId,
        contentId: quizId,
        language: contentLanguage
      }).then(() => {
        Metrics.counter('quiz-content-mutation', {
          action: 'quiz-content-progress-completed'
        }).increment();
      }).catch(err => {
        Metrics.counter('quiz-mutation-call-error', {
          action: 'quiz-content-progress-update-error'
        }).increment();
        reportErrorToLogFetch({
          loggingTitle: 'Quiz Content Complete Error',
          action: 'quiz-content-complete-error',
          error: err,
          additionalData: {
            contentId: quizId,
            lessonId,
            trackId
          }
        });
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.quiz.contentProgressError",
            options: {
              contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
            }
          })
        });
      });
      UsageTracker.trackStandalone('Learning Center Interaction', {
        action: 'finish-quiz',
        lessonId,
        quizId,
        language: contentLanguage
      });
      Metrics.counter('quiz-finished', {
        language: contentLanguage
      }).increment();
    }).catch(err => {
      Metrics.counter('quiz-mutation-call-error', {
        action: 'quiz-finish-error'
      }).increment();
      reportErrorToLogFetch({
        loggingTitle: 'Quiz Finish Error',
        action: 'quiz-finish-error',
        error: err,
        additionalData: {
          quizId,
          lessonId,
          trackId,
          attemptId: currentAttemptId
        }
      });
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.quiz.finaliseError",
          options: {
            contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
          }
        })
      });
    });
  }, [contentLanguage, currentAttemptId, finishQuiz, lessonId, quizId, track === null || track === void 0 ? void 0 : track.id, trackId, updateContentProgress]);
  const advanceQuiz = useCallback(() => {
    if (!maybeAnswerSubmissions || !questions) return;
    const hasAnsweredAllQuestions = maybeAnswerSubmissions.length >= questions.length;
    if (!hasCompleted && maybeAnswerSubmissions.length > questions.length) {
      Raven.captureMessage(`QuizContainer: Bounds Error`, {
        extra: {
          currentAttemptId,
          attemptIsInitializing: startQuizLoading,
          hasError: !!error,
          currentQuestionIndex,
          numAnswerSubmissions: maybeAnswerSubmissions.length,
          numQuestions: questions.length,
          maybeAnswerSubmissions: JSON.stringify(maybeAnswerSubmissions)
        }
      });
    }
    if (hasCompleted) {
      onFinish === null || onFinish === void 0 || onFinish();
    } else if (hasAnsweredAllQuestions) {
      finaliseAttempt();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswerIds([]);
    }
  }, [maybeAnswerSubmissions, questions, hasCompleted, currentAttemptId, startQuizLoading, error, currentQuestionIndex, onFinish, finaliseAttempt]);
  const checkAnswer = useCallback(() => {
    if (!currentQuestion) {
      return null;
    }
    const questionId = currentQuestion.id;
    if (!currentAttemptId) {
      return startQuiz(quizId, contentLanguage).then(attempt => {
        var _attempt$data;
        const result = (_attempt$data = attempt.data) === null || _attempt$data === void 0 ? void 0 : _attempt$data.result;
        if (!result) {
          return null;
        }
        UsageTracker.trackStandalone('Learning Center Interaction', {
          action: 'start-quiz',
          lessonId,
          quizId,
          language: contentLanguage
        });
        Metrics.counter('quiz-started', {
          language: contentLanguage
        }).increment();
        return submitQuizAnswer({
          lessonId,
          attemptId: result.id,
          questionId,
          answerIds: selectedAnswerIds,
          startQuizInfo: {
            quizId,
            firstAttemptData: result
          },
          trackId
        }).then(() => {
          UsageTracker.trackStandalone('Learning Center Interaction', {
            action: 'answer-quiz-question',
            lessonId,
            quizId,
            language: contentLanguage
          });
          Metrics.counter('quiz-question-answered', {
            language: contentLanguage
          }).increment();
        }).catch(err => {
          Metrics.counter('quiz-mutation-call-error', {
            action: 'quiz-answer-first-question-error'
          }).increment();
          reportErrorToLogFetch({
            loggingTitle: 'Quiz Answer First Question Error',
            action: 'quiz-answer-first-question-error',
            error: err,
            additionalData: {
              lessonId,
              quizId,
              language: contentLanguage
            }
          });
          FloatingAlertStore.addAlert({
            type: 'danger',
            message: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "learningCenter.quiz.answerQuestionError",
              options: {
                contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
              }
            })
          });
        });
      }).catch(err => {
        Metrics.counter('quiz-mutation-call-error', {
          action: 'quiz-start-error'
        }).increment();
        reportErrorToLogFetch({
          loggingTitle: 'Quiz Start Error',
          action: 'quiz-start-error',
          error: err,
          additionalData: {
            lessonId,
            quizId,
            language: contentLanguage
          }
        });
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.quiz.startQuizError",
            options: {
              contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
            }
          })
        });
      });
    } else {
      return submitQuizAnswer({
        lessonId,
        attemptId: currentAttemptId,
        questionId,
        answerIds: selectedAnswerIds,
        trackId
      }).then(() => {
        UsageTracker.trackStandalone('Learning Center Interaction', {
          action: 'answer-quiz-question',
          lessonId,
          quizId,
          language: contentLanguage
        });
        Metrics.counter('quiz-question-answered', {
          language: contentLanguage
        }).increment();
      }).catch(err => {
        Metrics.counter('quiz-mutation-call-error', {
          action: 'quiz-answer-question-error'
        }).increment();
        reportErrorToLogFetch({
          loggingTitle: 'Quiz Answer Question Error',
          action: 'quiz-answer-question-error',
          error: err,
          additionalData: {
            lessonId,
            quizId,
            language: contentLanguage
          }
        });
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "learningCenter.quiz.answerQuestionError",
            options: {
              contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
            }
          })
        });
      });
    }
  }, [contentLanguage, currentAttemptId, currentQuestion, lessonId, quizId, selectedAnswerIds, startQuiz, submitQuizAnswer, trackId]);
  const handleKeydown = useCallback(event => {
    if (!currentQuestion) return;
    const hasAnsweredCurrentQuestion = getHasAnsweredCurrentQuestion(currentQuestion.id);
    const {
      target
    } = event;
    if (event.keyCode === KeyCodes.ENTER && isProgressButtonDisabled && !target.id.includes('ignore-enter-proceed')) {
      if (hasAnsweredCurrentQuestion) {
        advanceQuiz();
      } else {
        checkAnswer();
      }
    }
  }, [advanceQuiz, checkAnswer, currentQuestion, getHasAnsweredCurrentQuestion, isProgressButtonDisabled]);
  useUnmountLifecycle(() => {
    const numQuestions = questions ? questions.length : null;
    const answeredQuestions = maybeAnswerSubmissions ? maybeAnswerSubmissions.length : null;
    const answeredAllQuestions = !!numQuestions && !!answeredQuestions && numQuestions <= answeredQuestions;
    if (currentAttemptId && !answeredAllQuestions) {
      clearQuizAttempt({
        trackId,
        lessonId,
        quizId,
        attemptId: currentAttemptId
      });
    }
  });
  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown]);
  useEffect(() => {
    setCurrentQuestionIndex(0);
    setSelectedAnswerIds([]);
  }, [quizId]);
  const handleRetakeQuizClick = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswerIds([]);
    if (currentAttemptId) {
      clearQuizAttempt({
        trackId,
        lessonId,
        quizId,
        attemptId: currentAttemptId
      });
      UsageTracker.trackStandalone('Learning Center Interaction', {
        action: 'retake-quiz',
        lessonId,
        quizId,
        language: contentLanguage
      });
      Metrics.counter('quiz-restarted', {
        language: contentLanguage
      }).increment();
    }
  };
  const handleTryAgainClick = () => {
    setSelectedAnswerIds([]);
  };
  const handleSelectedAnswersChange = AnswerIds => {
    setSelectedAnswerIds(AnswerIds);
  };
  const hasAnsweredCurrentQuestion = !!currentQuestion && getHasAnsweredCurrentQuestion(currentQuestion.id);
  if (loading) {
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "quiz-container",
      children: /*#__PURE__*/_jsx(QuizWrapper, {
        children: /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          blockSize: "100%",
          flexGrow: "1",
          justifyContent: "center",
          minBlockSize: "400px",
          children: /*#__PURE__*/_jsx(LoadingSpinner, {
            "data-test-id": "quiz-loading",
            grow: true
          })
        })
      })
    });
  }
  if (error || !questions || !currentQuestion) {
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "quiz-container",
      children: /*#__PURE__*/_jsx(QuizWrapper, {
        children: /*#__PURE__*/_jsx(QuizError, {
          progressToNextContent: onFinish
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "quiz-container",
    children: [/*#__PURE__*/_jsx(QuizWrapper, {
      children: hasCompleted ? /*#__PURE__*/_jsx(QuizEndScreen, {
        correctAnswers: (maybeAnswerSubmissions || []).filter(answerSubmission => answerSubmission.correctAnswer).length,
        onRetakeClick: handleRetakeQuizClick,
        totalQuestions: questions.length
      }) : /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(QuizTopbar, {
          currentQuestion: currentQuestionIndex + 1,
          numQuestions: questions.length,
          topRightComponent: /*#__PURE__*/_jsx(Link, {
            onClick: onFinish,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.quiz.skipQuiz"
            })
          })
        }), /*#__PURE__*/_jsx(QuizQuestionContainer, {
          lessonId: lessonId,
          onSelectAnswers: handleSelectedAnswersChange,
          onTryAgainClick: handleTryAgainClick,
          question: currentQuestion,
          quizId: quizId,
          recentQuizAttempt: recentQuizAttempt,
          selectedAnswerIds: selectedAnswerIds,
          trackId: trackId
        })]
      })
    }), /*#__PURE__*/_jsx(QuizControlBar, {
      buttonDisabled: isProgressButtonDisabled,
      hasAnsweredCurrentQuestion: hasAnsweredCurrentQuestion,
      onClick: hasAnsweredCurrentQuestion ? advanceQuiz : checkAnswer
    })]
  });
};
const QuizContent = _ref => {
  let {
      lesson
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return lesson ? /*#__PURE__*/_jsx(QuizContainer, Object.assign({
    lesson: lesson
  }, props)) : null;
};
export default QuizContent;